import {Product, ProductOrigin, ProductType} from "./product";
import {Utils} from "../../shared/utils/utils";
import {User} from "../../user/user";
import {ProductUpdateHistory} from "./productUpdateHistory";
import {Config} from "../../config/config";

export class ProductMapper {

  public static mapTenWebProduct(tenWebProductData: any, websiteProductData: any = null, isNetwork: boolean = false): Product {
    const product: Product = new Product();
    product.uid = tenWebProductData ? tenWebProductData.product_id : "";
    product.productId = tenWebProductData ? tenWebProductData.product_id : "";
    product.slug = tenWebProductData ? tenWebProductData.slug : "";
    product.type = tenWebProductData ? tenWebProductData.type : "";
    product.origin = ProductOrigin.TEN_WEB;
    product.service = tenWebProductData && tenWebProductData.service ? tenWebProductData.service.toLowerCase() : '';
    product.title = tenWebProductData.title;
    product.description = tenWebProductData.description;
    product.logo = tenWebProductData.logo;
    product.author = tenWebProductData.author;
    product.authorUrl = tenWebProductData.author_url;
    product.version = websiteProductData ? Utils.parseVersion(websiteProductData.version) : '';
    product.currentVersion = {
      free: tenWebProductData.current_version ? Utils.parseVersion(tenWebProductData.current_version.free) : '',
      paid: tenWebProductData.current_version ? Utils.parseVersion(tenWebProductData.current_version.paid) : ''
    };
    product.hasAddons = Utils.isTruthy(tenWebProductData.with_addons);
    product.downloadLink = tenWebProductData.download_link;
    product.downloads = tenWebProductData.downloads;
    product.demoLink = tenWebProductData.demo_link;
    product.installed = !!websiteProductData && Utils.isTruthy(websiteProductData.installed);
    product.hasPaidVersion = Utils.isTruthy(tenWebProductData.is_paid) || product.slug === 'form-maker' || product.slug === 'photo-gallery' || product.slug === 'slider-wd' || product.slug === 'tenweb-speed-optimizer';
    product.hasPaidVersionPremium = Utils.isTruthy(tenWebProductData.is_paid);
    product.availableForHosting = product.slug === 'form-maker' || product.slug === 'photo-gallery' || product.slug === 'slider-wd' || product.slug === 'tenweb-speed-optimizer'; // Utils.isTruthy(tenWebProductData.is_hosting_available); //Changes related to decision to have only Form Maker and Gallery (DBOARD-2924)
    product.paidVersionInstalled = !!websiteProductData && Utils.isTruthy(websiteProductData.installed) && Utils.isTruthy(websiteProductData.is_paid);
    product.paidVersionAvailable = Utils.isTruthy(tenWebProductData.is_paid) && !product.paidVersionInstalled;
    product.parentId = tenWebProductData.parent_id;
    product.active = !!websiteProductData && Utils.isTruthy(websiteProductData.active);
    product.networkActive = !!websiteProductData && Utils.isTruthy(websiteProductData.network_active);

    product.updateAvailable = product.paidVersionInstalled ? Utils.isUpdateAvailable(product.version, product.currentVersion.paid) : Utils.isUpdateAvailable(product.version, product.currentVersion.free);
    product.isNetwork = isNetwork;
    product.selected = false;
    product.popularRank = tenWebProductData.popular_rank;
    product.requiredWordpressVersion = tenWebProductData.requires && tenWebProductData.requires.required_specs ? tenWebProductData.requires.required_specs.wordpress : "";
    product.requires = tenWebProductData.requires;
    product.lastUpdated = tenWebProductData.last_updated ? Utils.getWordpressDate(tenWebProductData.last_updated) : null;
    product.rating = Math.round(parseFloat(tenWebProductData.rating) / 100 * 10) / 2;
    product.tenWebProduct = true;

    return product;
  }

  public static mapWordpressProduct(websiteProductData: any, productType: string, isNetwork: boolean = false): Product {
    const product: Product = new Product();

    product.uid = websiteProductData.slug;
    product.slug = websiteProductData.slug;
    product.type = productType;
    product.origin = product.slug === Config.managerPluginSlug ? ProductOrigin.TEN_WEB : ProductOrigin.WORDPRESS;
    product.title = websiteProductData.title;
    product.description = websiteProductData.description ? websiteProductData.description.replace(/<[^>]*>/g, '') : '';
    product.logo = product.type === ProductType.THEME ? "https://images.10web.io/0x0/" + websiteProductData.screenshot : websiteProductData.url !== "" ? websiteProductData.url : "../../../../assets/images/icons/plugin.png";
    product.author = websiteProductData.author;
    product.authorUrl = websiteProductData.author_profile;
    product.version = websiteProductData.version;
    product.currentVersion = {
      free: websiteProductData.repo_version
    };
    product.hasAddons = false;
    product.installed = Utils.isTruthy(websiteProductData.installed);
    product.paidVersionInstalled = false;
    product.hasPaidVersion = false;
    product.active = Utils.isTruthy(websiteProductData.active);
    product.networkActive = !!websiteProductData && Utils.isTruthy(websiteProductData.network_active);
    product.updateAvailable = product.paidVersionInstalled ? Utils.isUpdateAvailable(product.version, product.currentVersion.paid) : Utils.isUpdateAvailable(product.version, product.currentVersion.free);
    product.isNetwork = isNetwork;
    product.popularRank = websiteProductData.popular_rank || "";
    product.requiredWordpressVersion = websiteProductData.requires || "";
    product.activeInstalls = websiteProductData.active_installs;
    product.lastUpdated = websiteProductData.last_updated ? Utils.getWordpressDate(websiteProductData.last_updated) : null;
    product.rating = Math.round(parseFloat(websiteProductData.rating) / 100 * 10) / 2;
    product.requires = websiteProductData.requires || "";

    return product;
  }

  public static mapProducts(tenwebProductsData: any[], installedProductsData: any[] = [], isNetwork: boolean, productType: string, user: User): Product[] {
    const tenwebProductSlugs: string[] = tenwebProductsData.map((data: any): string => data.slug);
    const tenwebInstalledProductsMap: any = installedProductsData.reduce((installedProductsMap: any, data: any): any => {
      if (tenwebProductSlugs.includes(data.slug) && Utils.isTruthy(data.tenweb_product)) {
        installedProductsMap[data.slug] = data;
      }

      return installedProductsMap;
    }, {});

    const wordpressProductDataList: Product[] = installedProductsData.filter((data: any): boolean => !tenwebProductSlugs.includes(data.slug) && !Utils.isTruthy(data.tenweb_product));
    const tenwebProducts =  tenwebProductsData.map((productData: any): Product => {
      const installedProductData: any = tenwebInstalledProductsMap[productData.slug];

      const tenWebProduct: Product = this.mapTenWebProduct(productData, installedProductData, isNetwork);

      return tenWebProduct;
    });

    const wordpressProducts: Product[] = wordpressProductDataList.map((productData: any): Product => {
      const wordpressProduct: Product = this.mapWordpressProduct(productData, productType, isNetwork);

      return wordpressProduct;
    });

    let products: Product[] = tenwebProducts.concat(wordpressProducts);
    products = Utils.sort(products, (p1: Product, p2: Product): number => p1.activeInstalls === p2.activeInstalls ? 0 : (p1.activeInstalls > p2.activeInstalls ? 1 : -1));
    products = Utils.sort(products, (p1: Product, p2: Product): number => p1.hasPaidVersion === p2.hasPaidVersion ? 0 : (!p1.hasPaidVersion && p2.hasPaidVersion ? 1 : -1));

    return products;
  }

  public static mapWordpressPlugins(productsData: any[], installedProductsData: any[] = [], isNetwork: boolean): Product[] {
    const installedProductsMap: any = installedProductsData.reduce((installedProductsMap: any, productData: any): any => {
      installedProductsMap[productData.slug] = productData;
      return installedProductsMap;
    }, {});

    const products_data =  productsData.map((productData: any): Product => {
      const installedProductData: any = installedProductsMap[productData.slug];
      const product: Product = new Product();
      product.uid = productData.slug;
      product.slug = productData.slug;
      product.type = ProductType.PLUGIN;
      product.origin = product.slug === Config.managerPluginSlug ? ProductOrigin.TEN_WEB : ProductOrigin.WORDPRESS;
      product.title = productData.name;
      product.description = productData.short_description.replace(/<[^>]*>/g, '');
      product.logo = productData.icons["1x"] ? productData.icons["1x"] : productData.icons.default;
      product.author = productData.author.replace(/<[^>]*>/g, '');
      product.authorUrl = productData.author_profile;
      product.version = productData.version;
      product.downloadLink = productData.download_link;
      product.installed = !!installedProductData && Utils.isTruthy(installedProductData.installed);
      product.active = !!installedProductData && Utils.isTruthy(installedProductData.active);
      product.networkActive = !!installedProductData && Utils.isTruthy(installedProductData.network_active);

      // TODO CHANGE WHEN SOMETHING CHANGE ABOUT FREE PLUGINS
      product.updateAvailable = Utils.isUpdateAvailable(product.version, productData.version);
      product.isNetwork = isNetwork;
      product.selected = false;
      product.lastUpdated = productData.last_updated ? Utils.getWordpressDate(productData.last_updated
        .split('am').join('')
        .split('pm').join('')
        .split('GMT').join('')
        .trim()
      ) : null;
      product.popularRank = productData.popular_rank || "";
      product.rating = Math.round(parseFloat(productData.rating) / 100 * 10) / 2;
      product.activeInstalls = productData.active_installs;
      product.requiredWordpressVersion = productData.requires || "";

      return product;
    });

    return Utils.sort(products_data, (p1: Product, p2: Product): number => p1.popularRank === p2.popularRank ? 0 : (p1.popularRank > p2.popularRank ? 1 : -1));
  }

  public static mapWordpressThemes(productsData: any[], installedProductsData: any[] = [], isNetwork: boolean = false): Product[] {
    const installedProductsMap: any = installedProductsData.reduce((installedProductsMap: any, productData: any): any => {
      installedProductsMap[productData.slug] = productData;
      return installedProductsMap;
    }, {});
    const products_data =  productsData.map((productData: any): Product => {
      const installedProductData: any = installedProductsMap[productData.slug];
      const product: Product = new Product();
      product.uid = productData.slug;
      product.slug = productData.slug;
      product.type = ProductType.THEME;
      product.origin = product.slug === Config.tenWebThemeSlug ? ProductOrigin.TEN_WEB : ProductOrigin.WORDPRESS;
      product.title = productData.name;
      product.description = productData?.sections?.description.replace(/<[^>]*>/g, '') || productData?.description.replace(/<[^>]*>/g, '');
      product.logo = productData.screenshot_url;
      product.author = productData.author.replace(/<[^>]*>/g, '');
      product.version = productData.version;
      product.downloadLink = productData.homepage;
      product.installed = !!installedProductData && Utils.isTruthy(installedProductData.installed);
      product.active = !!installedProductData && Utils.isTruthy(installedProductData.active);
      product.networkActive = !!installedProductData && Utils.isTruthy(installedProductData.network_active);
      // TODO CHANGE WHEN SOMETHING CHANGE ABOUT FREE PLUGINS
      product.updateAvailable = Utils.isUpdateAvailable(product.version, productData.version);
      product.isNetwork = isNetwork;
      product.selected = false;
      product.lastUpdated = productData.last_updated ? Utils.getWordpressDate(productData.last_updated) : null;
      product.popularRank = productData.popular_rank || "";
      product.rating = Math.round(parseFloat(productData.rating) / 100 * 10) / 2;
      product.activeInstalls = productData.active_installs;
      product.requiredWordpressVersion = productData.requires || "";

      return product;
    });

    return Utils.sort(products_data, (p1: Product, p2: Product): number => p1.popularRank === p2.popularRank ? 0 : (p1.popularRank > p2.popularRank ? 1 : -1));
  }

  public static mapProductHistory(productUpdateHistories): ProductUpdateHistory {
    const productUpdateHistory: ProductUpdateHistory = new ProductUpdateHistory();
    productUpdateHistory.date = new Date(parseInt(productUpdateHistories.created_at, 10) * 1000);
    productUpdateHistory.name = productUpdateHistories.title;
    productUpdateHistory.oldVersion = productUpdateHistories.old_version;
    productUpdateHistory.newVersion = productUpdateHistories.new_version;

    return productUpdateHistory;
  }

}
